import { useEffect, useRef, useState } from "react"
import * as A from "../ApprovedPanel/styled"
import * as S from "./styled"

import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import loadingAnimation from "../../assets/animations/loading"

import { ReactComponent as CheckCircle } from "../../assets/icons/check_circle.svg"
import { ReactComponent as WhatsappIcon } from "../../assets/icons/whatsapp.svg"
import { FileText, Ticket } from "lucide-react"

import AskPhoneNumberModal from "../Modal/AskPhoneNumber"
import { Api, STORE_WHATSAPP_E164 } from "../../api"
import { logCommunication } from "../../utils/logCommunication"
import { formatWhatsapp } from "../../utils/tb/phone/maskWhatsapp"
import { onlyDigits } from "../../utils/tb/phone/onlyDigits"
import { orderTokenFor } from "../../utils/tb/orderToken"
import {
  buildRequestTicketsUrl,
  buildSaveTicketsUrl,
  buildTicketsLink,
  getOrderPublicCode,
} from "../../utils/tb/ticketsLink"

// "sending": esperando o resultado do envio automático do backend.
// "sent": o ingresso foi para o WhatsApp do cliente.
// "checking" | "request" | "save": fluxo de guardar (fallback), igual ao de
// antes do envio pelo backend.
type Mode = "sending" | "sent" | "checking" | "request" | "save"

type Props = {
  eventId: string
  eventName: string
  /** Data já formatada do evento, usada na mensagem do WhatsApp. */
  eventDate?: string
  /** ID do pedido usado no link público (mesmo de `Api.get.purchaseInfo`). */
  orderId: string
  /** Referência externa do pedido; origem do código curto "#ABC123". */
  extref: string
  quantity: number
  buyerName?: string
  buyerPhone?: string
  buyerEmail?: string | null
  onDownload: () => void
  /** Não usado no layout atual; mantido para compatibilidade com as páginas. */
  onSeeMyTickets?: () => void
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 25000

// Erros do reenvio que o cliente resolve no próprio modal (telefone, limite,
// sessão). Qualquer outro (502, 500, rede) leva ao fluxo de guardar.
const MODAL_ERROR_CODES = ["400", "429", "UNAUTHORIZED"]

// Sai do "sending" só se ainda estiver nele: o prazo e a resposta da consulta
// podem chegar no mesmo tick, e a primeira saída vale.
const leaveSending =
  (next: Mode) =>
  (current: Mode): Mode =>
    current === "sending" ? next : current

const openWhatsapp = (url: string) => {
  const w = window.open(url, "_blank")
  if (w) w.opener = null
  else window.location.href = url
}

const PostPaymentDelivery = ({
  eventId,
  eventName,
  eventDate,
  orderId,
  extref,
  quantity,
  buyerName = "",
  buyerPhone = "",
  buyerEmail,
  onDownload,
}: Props) => {
  const [mode, setMode] = useState<Mode>("sending")
  const [isAskingPhone, setIsAskingPhone] = useState(false)
  const [opened, setOpened] = useState(false)
  const [sentPhone, setSentPhone] = useState("")
  const [resentTo, setResentTo] = useState<string[]>([])
  const [resendError, setResendError] = useState("")
  // O envio automático foi tentado e não entregou (falhou, erro na consulta
  // ou ainda pendente no fim do prazo): o fluxo de guardar avisa o cliente.
  // Desligado ou nunca consultado: sem aviso.
  const [autoFailed, setAutoFailed] = useState(false)
  const pendingSeen = useRef(false)

  const publicCode = getOrderPublicCode(extref)
  const isReady = Boolean(orderId) && (mode !== "request" || publicCode !== "")

  // Prazo do envio automático, contado da montagem: o orderId pode chegar
  // depois (cartão e recuperação pelo "payed" aprovam antes de carregar o
  // pedido). Passou dos 25s sem "sent", cai no fluxo de guardar.
  useEffect(() => {
    if (mode !== "sending") return

    const timer = setTimeout(() => {
      if (pendingSeen.current) setAutoFailed(true)
      setMode(leaveSending("checking"))
    }, POLL_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [mode])

  // Resultado do envio automático: a cada 2s até o prazo acima. Só "sent"
  // mostra a tela de enviado; "disabled", "failed" e erro caem no fluxo de
  // guardar na hora. Sem orderId ainda, fica no carregando sem consultar.
  useEffect(() => {
    if (mode !== "sending" || !orderId || !eventId) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const poll = async () => {
      const res = await Api.get.voucherAutoStatus({ orderId, eventId })
      if (cancelled) return

      if (res.ok && res.data.status === "sent") {
        // Só é lido na tela de enviado; se o prazo ganhou, fica sem uso.
        setSentPhone(res.data.phone)
        setMode(leaveSending("sent"))
      } else if (res.ok && res.data.status === "pending") {
        pendingSeen.current = true
        timer = setTimeout(poll, POLL_INTERVAL_MS)
      } else {
        setAutoFailed(!res.ok || res.data.status === "failed")
        setMode(leaveSending("checking"))
      }
    }

    poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [mode, orderId, eventId])

  // Fluxo de guardar.
  useEffect(() => {
    if (mode !== "checking") return

    let cancelled = false

    Api.get.instanceStatus().then((res) => {
      if (cancelled) return
      // Sem número da loja configurado ou instância inativa/indisponível: o
      // fluxo "guardar no meu WhatsApp" funciona independente da instância.
      const active = res.ok && res.data.active && STORE_WHATSAPP_E164 !== ""
      setMode(active ? "request" : "save")
    })

    return () => {
      cancelled = true
    }
  }, [mode])

  const handleConfirm = async (phone: string): Promise<boolean> => {
    // window.open precisa rodar sincronamente no gesto do usuário (iOS/Safari).
    if (mode === "request") {
      if (!publicCode) return false
      openWhatsapp(buildRequestTicketsUrl(publicCode))
    } else {
      if (!orderId) return false
      openWhatsapp(
        buildSaveTicketsUrl({
          phone,
          quantity,
          eventName,
          eventDate,
          link: buildTicketsLink(eventName, orderId, orderTokenFor(orderId)),
        }),
      )
    }

    setOpened(true)

    logCommunication({
      event_id: String(eventId),
      order_id: orderId,
      buyer_name: buyerName,
      recipient: phone,
      channel: "whatsapp",
      status: "success",
    })

    return true
  }

  const handleResend = async (phone: string): Promise<boolean> => {
    setResendError("")
    const digits = onlyDigits(phone)
    const res = await Api.post.voucher.sendWhatsapp({
      orderId,
      eventId,
      phone: digits,
    })

    if (res.ok) {
      setResentTo((prev) => (prev.includes(digits) ? prev : [...prev, digits]))
      return true
    }

    if (res.code && MODAL_ERROR_CODES.includes(res.code)) {
      setResendError(res.error)
      return false
    }

    // O envio em si falhou: some o modal e entra o fluxo de guardar.
    setIsAskingPhone(false)
    setMode("checking")
    return false
  }

  const openResend = () => {
    setResendError("")
    setIsAskingPhone(true)
  }

  const modalCopy =
    mode === "request"
      ? {
          title: "Solicitar ingressos no WhatsApp",
          subtitle:
            "Confirme seu número. Vamos abrir o WhatsApp com uma mensagem pronta para você nos enviar e receber seus ingressos aqui.",
          helper: "Use o número do WhatsApp em que quer receber os ingressos.",
          confirmLabel: "Continuar",
          sendingLabel: "Abrindo...",
          successTitle: "Abrindo o WhatsApp...",
          successSubtext: "Toque em Enviar para concluir.",
        }
      : {
          title: "Guardar no WhatsApp",
          subtitle: "Confirme seu número e toque em continuar.",
          helper: "",
          confirmLabel: "Continuar",
          sendingLabel: "Abrindo...",
          successTitle: "Abrindo o WhatsApp...",
          successSubtext: "Toque em Enviar para guardar a mensagem.",
        }

  const isSent = mode === "sent"
  // Só o telefone informado pelo backend: o automático vai para o telefone do
  // cadastro, que pode não ser o do checkout. O do checkout só preenche o
  // modal quando o backend não informa um celular válido.
  const sentToLabel = formatWhatsapp(sentPhone)
  const sentDigits = sentToLabel ? onlyDigits(sentPhone) : ""
  // O número que recebeu o automático e os que já receberam reenvio pedem
  // confirmação antes de gastar um dos 5 envios.
  const alreadySent = [sentDigits, ...resentTo].filter(Boolean)

  const email = buyerEmail && buyerEmail.trim().length > 0 ? buyerEmail : null
  const qntLabel = `${quantity} ${quantity === 1 ? "ingresso" : "ingressos"}`

  return (
    <S.Card $k={3}>
      <AskPhoneNumberModal
        shown={isAskingPhone}
        basePhone={isSent && sentDigits ? sentDigits : buyerPhone}
        handleClose={() => setIsAskingPhone(false)}
        handleConfirm={isSent ? handleResend : handleConfirm}
        sentPhones={isSent ? alreadySent : []}
        error={isSent ? resendError : ""}
        copy={isSent ? undefined : modalCopy}
      />

      <S.HeroIcon>
        <CheckCircle aria-hidden="true" />
      </S.HeroIcon>

      <S.Heading>
        <S.Eyebrow>Pagamento aprovado</S.Eyebrow>
        <h1>Seus ingressos estão prontos!</h1>
      </S.Heading>

      <S.OrderCard>
        <div className="icon">
          <Ticket aria-hidden="true" />
        </div>
        <div className="info">
          <strong>{eventName}</strong>
          <span className="dot" aria-hidden="true">
            •
          </span>
          <span className="qnt">{qntLabel}</span>
        </div>
      </S.OrderCard>

      {mode === "sending" || mode === "checking" ? (
        <S.Loading role="status" aria-live="polite">
          <div style={{ width: 96 }}>
            <DotLottieReact
              data={loadingAnimation}
              loop
              autoplay
              width="100%"
            />
          </div>
          <span>Preparando seus ingressos...</span>
        </S.Loading>
      ) : isSent ? (
        <S.ActionsBlock>
          <S.DeliveredCard role="status" aria-live="polite">
            <S.DeliveredIcon>
              <WhatsappIcon aria-hidden="true" />
            </S.DeliveredIcon>
            <S.DeliveredBody>
              <S.DeliveredTitle>
                {sentToLabel ? (
                  <>
                    Enviamos seus ingressos para o WhatsApp
                    <S.PhoneChip>{sentToLabel}</S.PhoneChip>
                  </>
                ) : (
                  "Enviamos seus ingressos para o seu WhatsApp."
                )}
              </S.DeliveredTitle>
              <S.DeliveredNote>
                O PDF com os ingressos chega em instantes.
              </S.DeliveredNote>
            </S.DeliveredBody>
          </S.DeliveredCard>

          <S.MainAction type="button" onClick={onDownload}>
            <FileText aria-hidden="true" />
            Baixar em PDF
          </S.MainAction>

          <S.SecondaryAction type="button" onClick={openResend}>
            <WhatsappIcon aria-hidden="true" />
            Reenviar / outro número
          </S.SecondaryAction>

          <S.Hint>Não chegou? Confira o número e toque em Reenviar.</S.Hint>
        </S.ActionsBlock>
      ) : (
        <S.ActionsBlock>
          {autoFailed ? (
            <S.DeliveryWarning role="status">
              <span aria-hidden="true">⚠️ </span>
              Não conseguimos entregar no seu WhatsApp. Escolha abaixo como
              deseja salvar seus ingressos:
            </S.DeliveryWarning>
          ) : (
            <S.ChooseLabel>Escolha como deseja guardar:</S.ChooseLabel>
          )}

          <S.MainAction
            type="button"
            disabled={!isReady}
            onClick={() => setIsAskingPhone(true)}
          >
            <WhatsappIcon aria-hidden="true" />
            {mode === "request"
              ? "Solicitar no WhatsApp"
              : "Guardar no WhatsApp"}
          </S.MainAction>

          <S.SecondaryAction type="button" onClick={onDownload}>
            <FileText aria-hidden="true" />
            Baixar em PDF
          </S.SecondaryAction>

          {opened && (
            <S.Hint>Se o WhatsApp não abriu, toque no botão novamente.</S.Hint>
          )}
        </S.ActionsBlock>
      )}

      {email && (
        <A.EmailNote>
          <span>Confirmação enviada para {email}</span>
        </A.EmailNote>
      )}
    </S.Card>
  )
}

export default PostPaymentDelivery
